/**
 * UpgradePrompt Component
 * 
 * Contextual upgrade prompts shown at key conversion moments:
 * - After analysis (especially NEGOTIATE/PASS results)
 * - When approaching limit
 * - After multiple analyses
 * 
 * Includes value-based messaging to show users what they've gained.
 * 
 * Bilingual: EN/ES
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, TrendingUp, Shield, ArrowRight, X, BarChart3, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { analyticsAPI } from '../api';

const translations = {
  en: {
    // Value messages
    valueSaved: "You've analyzed {count} deals — this likely saved you from making a bad investment.",
    valueProtected: "CHEKSTE helped you identify {passCount} deals that don't meet your criteria.",
    valueInsight: "You've gained insights on {count} properties. Keep analyzing before risking your money.",
    
    // Contextual prompts
    afterPassTitle: "Smart Decision",
    afterPassMessage: "You avoided a deal that doesn't meet your criteria. Keep analyzing to find the right opportunity.",
    afterNegotiateTitle: "Room to Negotiate",
    afterNegotiateMessage: "This deal could work with better terms. Upgrade to save and compare multiple scenarios.",
    afterBuyTitle: "Great Find!",
    afterBuyMessage: "This looks like a solid deal. Save it and keep analyzing to compare with other opportunities.",
    
    // Limit approaching
    limitApproachingTitle: "Running Low on Analyses",
    limitApproachingMessage: "You have {remaining} analyses left this month. Upgrade to analyze more deals.",
    
    // General upgrade
    upgradeTitle: "Unlock More Analyses",
    upgradeMessage: "Get unlimited access to property analysis, deal comparisons, and more.",
    
    // CTAs
    ctaUpgrade: "Upgrade Now",
    ctaViewPlans: "View Plans",
    ctaDismiss: "Maybe Later",
    
    // Stats
    dealsAnalyzed: "Deals Analyzed",
    badDealsAvoided: "Bad Deals Avoided",
    timeSaved: "Time Saved"
  },
  es: {
    // Value messages
    valueSaved: "Has analizado {count} tratos — esto probablemente te salvó de una mala inversión.",
    valueProtected: "CHEKSTE te ayudó a identificar {passCount} tratos que no cumplen tus criterios.",
    valueInsight: "Has obtenido información sobre {count} propiedades. Sigue analizando antes de arriesgar tu dinero.",
    
    // Contextual prompts
    afterPassTitle: "Decisión Inteligente",
    afterPassMessage: "Evitaste un trato que no cumple tus criterios. Sigue analizando para encontrar la oportunidad correcta.",
    afterNegotiateTitle: "Espacio para Negociar",
    afterNegotiateMessage: "Este trato podría funcionar con mejores términos. Actualiza para guardar y comparar múltiples escenarios.",
    afterBuyTitle: "¡Buen Hallazgo!",
    afterBuyMessage: "Este parece un trato sólido. Guárdalo y sigue analizando para comparar con otras oportunidades.",
    
    // Limit approaching
    limitApproachingTitle: "Análisis Casi Agotados",
    limitApproachingMessage: "Te quedan {remaining} análisis este mes. Actualiza para analizar más tratos.",
    
    // General upgrade
    upgradeTitle: "Desbloquea Más Análisis",
    upgradeMessage: "Obtén acceso ilimitado a análisis de propiedades, comparaciones de tratos y más.",
    
    // CTAs
    ctaUpgrade: "Actualizar Ahora",
    ctaViewPlans: "Ver Planes",
    ctaDismiss: "Quizás Después",
    
    // Stats
    dealsAnalyzed: "Tratos Analizados",
    badDealsAvoided: "Malos Tratos Evitados",
    timeSaved: "Tiempo Ahorrado"
  }
};

// Variant configurations
const variants = {
  // After PASS result
  afterPass: {
    icon: Shield,
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    borderColor: 'border-red-200',
    titleKey: 'afterPassTitle',
    messageKey: 'afterPassMessage'
  },
  // After NEGOTIATE result
  afterNegotiate: {
    icon: AlertTriangle,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    borderColor: 'border-amber-200',
    titleKey: 'afterNegotiateTitle',
    messageKey: 'afterNegotiateMessage'
  },
  // After BUY result
  afterBuy: {
    icon: CheckCircle2,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    borderColor: 'border-emerald-200',
    titleKey: 'afterBuyTitle',
    messageKey: 'afterBuyMessage'
  },
  // Limit approaching
  limitApproaching: {
    icon: BarChart3,
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
    borderColor: 'border-orange-200',
    titleKey: 'limitApproachingTitle',
    messageKey: 'limitApproachingMessage'
  },
  // General upgrade
  general: {
    icon: Zap,
    iconBg: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
    borderColor: 'border-indigo-200',
    titleKey: 'upgradeTitle',
    messageKey: 'upgradeMessage'
  }
};

const UpgradePrompt = ({
  variant = 'general',
  usage = null,
  dealStats = null,
  userId = null,
  showValue = true,
  showDismiss = true,
  onDismiss = () => {},
  onUpgrade = null,
  lang = 'en'
}) => {
  const navigate = useNavigate();
  const t = translations[lang] || translations.en;
  const config = variants[variant] || variants.general;
  const Icon = config.icon;
  
  // Calculate value stats
  const analysesCount = usage?.analyses_used || dealStats?.total || 0;
  const passCount = dealStats?.PASS || dealStats?.NO_PASS || 0;
  const negotiateCount = dealStats?.NEGOTIATE || dealStats?.MARGINAL || 0;
  const buyCount = dealStats?.BUY || dealStats?.GOOD_DEAL || 0;
  const remaining = usage?.analyses_remaining || 0;
  
  // Track prompt shown
  useEffect(() => {
    if (userId) {
      analyticsAPI.trackUpgradePromptShown(userId, variant, 'results_page', variant).catch(() => {});
    }
  }, [userId, variant]);
  
  // Get dynamic message with replacements
  const getMessage = () => {
    let message = t[config.messageKey];
    message = message.replace('{remaining}', remaining.toString());
    message = message.replace('{count}', analysesCount.toString());
    message = message.replace('{passCount}', passCount.toString());
    return message;
  };
  
  // Get value message
  const getValueMessage = () => {
    if (passCount > 0) {
      return t.valueProtected.replace('{passCount}', passCount.toString());
    }
    if (analysesCount > 1) {
      return t.valueInsight.replace('{count}', analysesCount.toString());
    }
    return t.valueSaved.replace('{count}', analysesCount.toString());
  };
  
  // Handle upgrade click
  const handleUpgrade = () => {
    if (userId) {
      analyticsAPI.trackUpgradeClick(userId, `upgrade_prompt_${variant}`, usage?.plan_type || 'FREE', {
        analyses_used: analysesCount,
        pass_count: passCount
      }).catch(() => {});
    }
    if (onUpgrade) {
      onUpgrade();
    } else {
      navigate('/billing');
    }
  };
  
  const isFreeUser = !usage || usage.plan_type === 'FREE';
  
  return (
    <div 
      className={`bg-white rounded-xl border-2 ${config.borderColor} shadow-lg overflow-hidden`}
      data-testid={`upgrade-prompt-${variant}`}
    >
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${config.iconBg}`}>
            <Icon className={`w-5 h-5 ${config.iconColor}`} />
          </div>
          <div>
            <h4 className="font-semibold text-slate-800">
              {t[config.titleKey]}
            </h4>
            <p className="text-sm text-slate-600 mt-0.5">
              {getMessage()}
            </p>
          </div>
        </div>
        {showDismiss && (
          <button
            onClick={onDismiss}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            data-testid="dismiss-prompt-btn"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
      
      {/* Value Stats (optional) */}
      {showValue && analysesCount > 0 && (
        <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-slate-50 to-slate-100 border-y border-slate-200">
          <p className="text-sm text-slate-700 font-medium flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-500" />
            {getValueMessage()}
          </p>
          
          {/* Mini stats */}
          {analysesCount > 1 && (
            <div className="flex items-center gap-4 mt-2">
              <div className="text-center">
                <div className="text-lg font-bold text-slate-800">{analysesCount}</div>
                <div className="text-xs text-slate-500">{t.dealsAnalyzed}</div>
              </div>
              {passCount > 0 && (
                <div className="text-center">
                  <div className="text-lg font-bold text-red-600">{passCount}</div>
                  <div className="text-xs text-slate-500">{t.badDealsAvoided}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* CTAs */}
      <div className="px-4 sm:px-6 py-4 flex flex-col sm:flex-row gap-2">
        <button
          onClick={handleUpgrade}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all shadow-md"
          data-testid="upgrade-btn"
        >
          <Zap className="w-4 h-4" />
          {t.ctaViewPlans}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default UpgradePrompt;
